import { useEffect, useState } from 'react';
import type { Track } from '../../api.js';

const TICK_MS = 2500;
const FALLBACK_BUILD_MS = 12 * 60 * 1000;
const MIN_SAMPLES = 3;
const MAX_SAMPLES = 10;
const CAP_MARGIN = 5;

const lastShown = new Map<string, number>();

function toMs(iso: string | null | undefined): number | null {
  return iso ? new Date(iso).getTime() : null;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function phaseDurations(
  tracks: Track[],
  pick: (t: Track) => [string | null, string | null],
  filter?: (t: Track) => boolean
): number[] {
  return tracks
    .filter((t) => (filter ? filter(t) : true))
    .map((t) => {
      const [fromIso, toIso] = pick(t);
      const from = toMs(fromIso);
      const to = toMs(toIso);
      return from != null && to != null ? to - from : null;
    })
    .filter((v): v is number => v != null && v > 0)
    .slice(0, MAX_SAMPLES);
}

function medianOrFallback(durations: number[]): number {
  const m = durations.length >= MIN_SAMPLES ? median(durations) : null;
  return m ?? FALLBACK_BUILD_MS;
}

function ease(elapsedMs: number, medianMs: number, from: number, to: number): number {
  if (elapsedMs <= 0) return from;
  const frac = Math.min(1, elapsedMs / medianMs);
  return Math.min(to - CAP_MARGIN, from + frac * (to - from));
}

function computeRawProgress(track: Track, recentCompleted: Track[], now: number): number {
  if (track.deploy_status === 'SUCCESS') return 100;

  if (track.merged_at) {
    const elapsed = now - (toMs(track.merged_at) ?? now);
    const med = medianOrFallback(
      phaseDurations(
        recentCompleted,
        (t) => [t.merged_at, t.deploy_checked_at],
        (t) => t.deploy_status === 'SUCCESS'
      )
    );
    return ease(elapsed, med, 90, 100);
  }

  if (track.built_at) {
    const elapsed = now - (toMs(track.built_at) ?? now);
    const med = medianOrFallback(phaseDurations(recentCompleted, (t) => [t.built_at, t.merged_at]));
    return ease(elapsed, med, 70, 90);
  }

  if (track.approved_at) {
    const med = medianOrFallback(phaseDurations(recentCompleted, (t) => [t.approved_at, t.built_at]));
    if (track.state === 'BUILDING') {
      const elapsed = now - (toMs(track.updated_at) ?? now);
      return ease(elapsed, med, 30, 70);
    }
    const elapsed = now - (toMs(track.approved_at) ?? now);
    return ease(elapsed, med, 10, 30);
  }

  return 10;
}

export function useProgress(track: Track, recentCompleted: Track[]): number {
  const [, tick] = useState(0);

  useEffect(() => {
    if (track.state !== 'BUILDING') return;
    const id = setInterval(() => tick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, [track.state]);

  const raw = computeRawProgress(track, recentCompleted, Date.now());
  const prev = lastShown.get(track.id) ?? 0;
  const next = Math.max(prev, Math.round(raw));
  lastShown.set(track.id, next);
  return next;
}
